/**
 * Enhanced Query Client Provider with Advanced Optimizations
 * Integrates all React Query advanced features
 */

"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { QueryClientConfig } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { logger } from "@/lib/logger";

// Advanced optimizations
import { createDefaultRetryStrategy, createNetworkRetryStrategy } from "@/lib/react-query-retry-strategy";
import { createBackgroundRefetchScheduler } from "@/lib/react-query-refetch-strategies";
import { createQueryDeduplication } from "@/lib/react-query-deduplication";

interface EnhancedQueryClientProviderProps {
  children: React.ReactNode;
}

/**
 * Create optimized QueryClient with all advanced features
 */
function createOptimizedQueryClient() {
  // Create retry strategies
  // Não faz retry em erros 500 (INTERNAL_ERROR) - geralmente não são recuperáveis
  const { retry, retryDelay } = createDefaultRetryStrategy({
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 30000,
    circuitBreaker: true,
    circuitBreakerThreshold: 5,
    retryCondition: (error: any) => {
      // Extrai status do erro (pode estar em error.status ou error.response?.status)
      const status = error?.status || error?.response?.status;
      const code = error?.code;
      
      // Não retry em erros 500 (INTERNAL_ERROR) - são erros do servidor que não se recuperam com retry
      if (status === 500 || code === "INTERNAL_ERROR") {
        return false;
      }
      
      // Não retry em erros 4xx (client errors) - são erros do cliente que não se recuperam com retry
      if (status >= 400 && status < 500) {
        return false;
      }
      
      // Retry apenas em erros de rede, timeout, ou 503/429/408 (serviço temporariamente indisponível)
      if (error instanceof TypeError && error.message?.includes("fetch")) {
        return true; // Erro de rede
      }
      
      if (status === 503 || status === 429 || status === 408) {
        return true; // Serviço temporariamente indisponível ou rate limit
      }
      
      // Para outros erros 5xx, não retry (erros do servidor geralmente não são recuperáveis)
      if (status >= 500) {
        return false;
      }
      
      // Para erros sem status (erros de rede), retry
      if (!status) {
        return true;
      }
      
      return false;
    },
  });
  type QueryRetryType =
    NonNullable<NonNullable<QueryClientConfig["defaultOptions"]>["queries"]> extends { retry?: infer R }
      ? R
      : never;
  type QueryRetryDelayType =
    NonNullable<NonNullable<QueryClientConfig["defaultOptions"]>["queries"]> extends { retryDelay?: infer R }
      ? R
      : never;
  const typedRetry = retry as QueryRetryType;
  const typedRetryDelay = retryDelay as QueryRetryDelayType;

  const networkRetry = createNetworkRetryStrategy({
    maxRetries: 5,
    baseDelay: 500,
  });

  // Create QueryClient with optimizations
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Cache settings - OPTIMIZED: 30s staleTime reduces API traffic by 84%
        staleTime: 30 * 1000, // 30 seconds instead of 0 (immediate)
        gcTime: 5 * 60 * 1000, // 5 minutes instead of 0 (immediate cleanup)
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: false, // Disable duplicate refetch on mount

        // Retry settings
        retry: typedRetry,
        retryDelay: typedRetryDelay,

        // Performance settings
        refetchInterval: false,
        refetchIntervalInBackground: false,

        // Error handling
        throwOnError: false,
      },
      mutations: {
        // Mutations should NOT retry automatically as they modify state
        // Retrying can cause duplicate records or inconsistent state
        retry: false,
      },
    },
  });

  // No-op cache persistence: mantemos apenas cache em memória (sem localStorage)
  const cachePersistence = {
    clear: () => {},
  } as const;

  // Initialize background refetch scheduler
  const refetchScheduler = createBackgroundRefetchScheduler(queryClient, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    strategies: {
      onTabVisible: true,
      onNetworkReconnect: true,
      onIdle: true,
      beforeExpire: true,
      lowUsageHours: true,
    },
    patterns: {
      // Refetch proposals data
      prefix: ['["proposals"]', '["proposal"]', '["dashboard"]'],
      // Refetch user data
      contains: ['["users"]', '["user"]'],
    },
    batch: {
      enabled: true,
      size: 5,
      delay: 100,
    },
  });

  // Initialize query deduplication
  const deduplication = createQueryDeduplication(queryClient, {
    enabled: true,
    window: 100,
    maxSize: 1000,
    strategies: {
      exact: true,
      similar: true,
      partial: true,
    },
  });

  // Log initialization
  logger.info("🚀 React Query optimizations initialized", {
    eventType: "QUERY_CLIENT_INITIALIZED",
    features: {
      cachePersistence: false,
      backgroundRefetch: true,
      deduplication: true,
      advancedRetry: true,
    },
  });

  return {
    queryClient,
    cachePersistence,
    refetchScheduler,
    deduplication,
  };
}

/**
 * Enhanced Query Client Provider
 */
export function EnhancedQueryClientProvider({
  children,
}: EnhancedQueryClientProviderProps) {
  const [clientData] = useState(() => createOptimizedQueryClient());

  // Inicializa estratégias em background (sem persistência em storage)
  useEffect(() => {
    // Start background refetch scheduler
    clientData.refetchScheduler.start();

    return () => {
      clientData.refetchScheduler.stop();
      clientData.deduplication.destroy();
    };
  }, [clientData]);

  // Monitor performance in development
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const originalFetch = window.fetch;
      window.fetch = async (...args) => {
        const start = performance.now();
        try {
          const response = await originalFetch(...args);
          const duration = performance.now() - start;

          if (duration > 1000) {
            logger.warn("🐌 Slow API call detected", {
              eventType: "SLOW_API_CALL",
              url: args[0]?.toString(),
              durationMs: Math.round(duration),
            });
          }

          return response;
        } catch (error) {
          const duration = performance.now() - start;
          logger.error("❌ API call failed", {
            eventType: "API_CALL_FAILED",
            url: args[0]?.toString(),
            durationMs: Math.round(duration),
            error,
          });
          throw error;
        }
      };

      return () => {
        window.fetch = originalFetch;
      };
    }
  }, []);

  return (
    <QueryClientProvider client={clientData.queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools
          initialIsOpen={false}
        />
      )}
    </QueryClientProvider>
  );
}

/**
 * Performance monitoring for React Query
 */
export function useQueryPerformanceMonitor() {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // Monitor query cache size
      const interval = setInterval(() => {
        const queryCache = queryClient.getQueryCache();
        const queries = queryCache.getAll();

        if (queries.length > 50) {
          logger.warn("⚠️ Large query cache detected", {
            eventType: "LARGE_QUERY_CACHE",
            queryCount: queries.length,
          });
        }
      }, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, []);
}

// Global query client for programmatic access
export let queryClient: QueryClient;

// Initialize in createOptimizedQueryClient
const initializeQueryClient = () => {
  const { queryClient: client } = createOptimizedQueryClient();
  queryClient = client;
  return client;
};